/**
 * Environment Configuration
 *
 * Expected Vite variables:
 * - VITE_NODE_ENV — development | staging | production (drives absolute API host when not on localhost proxy)
 * - VITE_DEVELOPMENT, VITE_STAGING, VITE_PRODUCTION — backend origin URLs for each stage
 * - VITE_FORCE_ABSOLUTE_API — when 'true', skip localhost/vidhyantra.local relative `/api` proxy behavior
 */

import { isLocalDevHostname } from '@/utils/cookie-domain.util'
import { resolveDeployBaseDomainFromHostname } from '@/utils/deploy-domain.util'

const NODE_ENV = import.meta.env.VITE_NODE_ENV
const FORCE_ABSOLUTE_API = import.meta.env.VITE_FORCE_ABSOLUTE_API === 'true'
const BACKEND_DEVELOPMENT = import.meta.env.VITE_DEVELOPMENT
const BACKEND_STAGING = import.meta.env.VITE_STAGING
const BACKEND_PRODUCTION = import.meta.env.VITE_PRODUCTION

/** Prefer live hostname over stale VITE_* bake (e.g. old vidyantra-dev.com Jenkins env). */
function resolveBackendUrlFromHostname(hostname: string): string | null {
  const baseDomain = resolveDeployBaseDomainFromHostname(hostname)
  if (baseDomain) {
    return `https://api.${baseDomain}`
  }
  return null
}

/**
 * Get the correct backend URL based on NODE_ENV
 * In development on localhost / vidhyantra.local, use a relative origin so axios
 * hits `/api` on the Vite host and the proxy forwards to the backend.
 * Direct `http://127.0.0.1:8080` from an HTTPS page is mixed-content and surfaces as
 * "Unable to reach the server".
 */
export const getBackendUrl = (): string => {
  if (typeof window !== 'undefined' && !FORCE_ABSOLUTE_API) {
    const fromHost = resolveBackendUrlFromHostname(window.location.hostname)
    if (fromHost !== null) {
      return fromHost
    }
    if (isLocalDevHostname(window.location.hostname)) {
      return ''
    }
  }

  if (NODE_ENV === 'development') {
    return BACKEND_DEVELOPMENT
  } else if (NODE_ENV === 'staging') {
    return BACKEND_STAGING
  } else if (NODE_ENV === 'production') {
    return BACKEND_PRODUCTION
  }
  return BACKEND_DEVELOPMENT
}

/**
 * API Base URL
 * Matches the old API_BASE_URL format
 * In development on localhost, uses relative URL (/api) to go through Vite proxy
 */
export const API_BASE_URL = (() => {
  const backendUrl = getBackendUrl()
  return `${backendUrl}/api`
})()

/**
 * Export individual values
 */
export { NODE_ENV, BACKEND_DEVELOPMENT, BACKEND_STAGING }

/** True for local Vite dev or VITE_NODE_ENV development/staging — hidden in production builds. */
export function isDevOrStagingEnvironment(): boolean {
  if (import.meta.env.DEV) return true
  return NODE_ENV === 'development' || NODE_ENV === 'staging'
}

